import { Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { disabled, form, FormField } from '@angular/forms/signals';

import { Location, UpperCasePipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatFormField } from '@angular/material/form-field';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { UserService } from '../../shared/services/user.service';
import { ScoutAccessRightsListModel, UpdateUserModel } from '../../shared/models/user.model';
import { MatToolbar, MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';


@Component({
    selector: 'app-config-user-details',
    templateUrl: './config-user-details.component.html',
    styleUrls: ['./config-user-details.component.css'],
  imports: [FormField, MatInput, MatFormField, MatCheckbox, RouterLink, TranslatePipe, UpperCasePipe,
    MatButton, MatSelectModule, MatToolbarModule, MatIconModule, MatButtonModule]
})
export class ConfigUserDetailsComponent {

  private _userId: string;
  readonly user = signal<UpdateUserModel | null>(null);
  readonly userModel = signal<UpdateUserModel>(ConfigUserDetailsComponent.createEmptyUserModel());
  readonly userForm = form(this.userModel, (user) => {
    disabled(user.Email!, ({ valueOf }) => !!valueOf(user.ConnectedPlayer!));
    disabled(user.FirstName!, ({ valueOf }) => !!valueOf(user.ConnectedPlayer!));
  });

  back(){
    this._location.back();
  }  

  readonly scoutingRoleControls: ReadonlyArray<{ field: keyof UpdateUserModel; label: string }> = [
    { field: 'Role_ScoutingAdmin', label: 'Scouting administrator' },
    { field: 'RoleScout', label: 'Scout' },
    { field: 'RoleAllReports', label: 'Reports and notes - ALL' },
    { field: 'RoleAllGameRatings', label: 'Game grades - ALL' },
    { field: 'RoleAllCalendarEvents', label: 'Calendar - ALL' },
    { field: 'RoleAllPlayerTags', label: 'Player tags - ALL' },
    { field: 'RoleStatisticsViewer', label: 'Statistics - ALL' },
    { field: 'RoleStatisticsEditor', label: 'Statistics Editor' },
    { field: 'RoleOrgPlayeradmin', label: 'Add player to roster' },
    { field: 'Role_ContactsEditor', label: 'Player contacts editor' },
    { field: 'Role_ContactsViewer', label: 'Player contacts viewer' },
  ];

  readonly userSecurityControls: ReadonlyArray<{ field: keyof ScoutAccessRightsListModel; label: string }> = [
    { field: 'ReportAccessIschecked', label: 'Reports' },
    { field: 'NoteAccessIschecked', label: 'Notes' },
    { field: 'GameRatingAccessIschecked', label: 'Grades' },
    { field: 'StatisticsAccessIschecked', label: 'Statistics' },
  ];


  constructor(
      public userSrv: UserService,
      public _route: ActivatedRoute,
      private _router: Router,
      private _location: Location,
      private destroyRef: DestroyRef ) {
    this._route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this._userId = params['UserId'];
      userSrv.getUser(this._userId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(u=>{
        const loadedUser = this.normalizeUser(u as UpdateUserModel);
        this.user.set(loadedUser);
        this.userModel.set(loadedUser);
      });

    });

  }

  private static createEmptyUserModel(): UpdateUserModel {
    return {
      UserName: undefined,
      LastName: undefined,
      FirstName: undefined,
      IsLocked: false,
      Email: undefined,
      WebCalendarEnabled: false,
      UserBgColor: undefined,
      RoleScout: false,
      RoleOrgadmin: false,
      RoleAllReports: false,
      RoleAllGameRatings: false,
      RoleAllCalendarEvents: false,
      RoleAllPlayerTags: false,
      RoleStatisticsEditor: false,
      RoleStatisticsViewer: false,
      RoleOrgPlayeradmin: false,
      Role_Load_All: false,
      Role_Load_AllPlayerGameRankings: false,
      Role_Load_AllPhysicalStatus: false,
      Role_TestAdmin: false,
      Role_TestPerformer: false,
      Role_TestViewer: false,
      Role_StatisticsAdmin: false,
      Role_StatisticsEditor: false,
      Role_StatisticsViewer: false,
      Role_ContactsEditor: false,
      Role_ContactsViewer: false,
      Role_PerformanceViewer: false,
      Role_MedicalAdmin: false,
      Role_MedicalViewer: false,
      Role_TeamManager: false,
      Role_ScoutingAdmin: false,
      Role_ScoutingScout: false,
      Role_ObjectiveEditor: false,
      Role_TrainingMgr: false,
      Role_UserAdmin: false,
      Role_FormsAdmin: false,
      Role_FormsSubmitter: false,
      Role_FormsViewer: false,
      LoadAccessRights: [],
      UserSecurity: [],
      ConnectedPlayer: undefined,
    };
  }

  private normalizeUser(user: UpdateUserModel): UpdateUserModel {
    return {
      ...ConfigUserDetailsComponent.createEmptyUserModel(),
      ...user,
      LoadAccessRights: user.LoadAccessRights ?? [],
      UserSecurity: this.normalizeUserSecurity(user.UserSecurity),
    };
  }

  private normalizeUserSecurity(
    security: ScoutAccessRightsListModel[] | undefined,
  ): ScoutAccessRightsListModel[] {
    return (security ?? []).map((entry) => ({
      UserId: entry.UserId,
      Value: entry.Value,
      ReportAccessIschecked: !!entry.ReportAccessIschecked,
      NoteAccessIschecked: !!entry.NoteAccessIschecked,
      GameRatingAccessIschecked: !!entry.GameRatingAccessIschecked,
      StatisticsAccessIschecked: !!entry.StatisticsAccessIschecked,
    }));
  }

  controlField(fieldName: keyof UpdateUserModel) {
    return this.userForm[fieldName] as any;
  }

  rowField(item: unknown, fieldName: string) {
    const rowControl = item as Record<string, unknown>;
    const directField = rowControl?.[fieldName];
    if (directField) {
      return directField as any;
    }

    if (typeof item === 'function') {
      const rowValue = (item as () => Record<string, unknown>)();
      return rowValue?.[fieldName] as any;
    }

    return undefined as any;
  }

  isUserSecurityChecked(
    index: number,
    fieldName: keyof ScoutAccessRightsListModel,
  ): boolean {
    const item = this.userModel().UserSecurity?.[index];
    return !!item?.[fieldName];
  }

  onUserSecurityChanged(
    index: number,
    fieldName: keyof ScoutAccessRightsListModel,
    checked: boolean,
  ) {
    this.userModel.update((current) => {
      const updatedSecurity = [...(current.UserSecurity ?? [])];
      const row = { ...(updatedSecurity[index] ?? {}) } as ScoutAccessRightsListModel;
      switch (fieldName) {
        case 'ReportAccessIschecked':
          row.ReportAccessIschecked = checked;
          break;
        case 'NoteAccessIschecked':
          row.NoteAccessIschecked = checked;
          break;
        case 'GameRatingAccessIschecked':
          row.GameRatingAccessIschecked = checked;
          break;
        case 'StatisticsAccessIschecked':
          row.StatisticsAccessIschecked = checked;
          break;
        default:
          break;
      }
      updatedSecurity[index] = row;

      return {
        ...current,
        UserSecurity: updatedSecurity,
      };
    });
  }

  isFirstNameMissing(): boolean {
    const firstName = this.userModel().FirstName;
    return !firstName || firstName.trim().length === 0;
  }

  isLastNameMissing(): boolean {
    const lastName = this.userModel().LastName;
    return !lastName || lastName.trim().length === 0;
  }

  canSaveUser(): boolean {
    return !this.isFirstNameMissing() && !this.isLastNameMissing();
  }



  saveUser(){
    if (!this.canSaveUser()) {
      return;
    }

    const updatedUser = this.userModel();
    //console.log("Saving user:");
    //console.log(updatedUser);
    this.userSrv.saveUser(updatedUser).subscribe(data =>{
      var success = data as Boolean;
      // console.log("Save user result: " + success);
      if(success) {
        this._router.navigate(["/admin/scouts"]);
        
      }else{
        alert("Error saving user");
      }

    });
    
  }

  cancel(){
    this._router.navigate(["/admin/scouts"]);
  }

  
}
